import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { uniqBy } from 'lodash';
import {
  Box,
  Stack,
  Typography,
  DatePicker,
  FormControlLabel,
  Checkbox,
  Button,
  Popover,
  useMediaQuery,
  Tooltip,
  SegmentedFilter,
} from '../../ui/core';
import { cardHeaderSx, filterPillSx, floatSurfaceSx, useTheme } from '../../ui/styles';
import Badge from '@mui/material/Badge';
import { ExpandMoreIcon, WarningIcon } from '../../ui/icons';

import { useQuery, useActiveExclusionMatchSet, useToast, useFormatters } from '../../hooks';
import { useHasPermission } from '../../helpers/api/auth/index';
import { TripDisplayStatus, TripDisplayStatusOrder } from '../../models/Trips';
import { filterDailyTrips } from '../../helpers/utils/trip';
import { todayInOpsTimezone } from '../../helpers/utils/opsTime';
import { getExcludedRiderIds } from '../../helpers/utils/riderDriverExclusions';
import { findUnresolvedTrips, getMassBonusTrips } from '../../helpers/utils/massBonus';

import { getDailyTrips } from '../../redux/Trips/actions';
import { getTags } from '../../redux/Tags/actions';

import TripDetailsDialog from '../../components/widgets/Trips/TripDetailsDialog';
import TripsTable, { TRIP_COLUMNS } from '../../components/widgets/Trips/TripsTable';
import TripsMobileList from '../../components/widgets/Trips/TripsMobileList';
import TripsMapView from '../../components/widgets/Trips/TripsMapView';
import { TripDisplayStatusIcon } from '../../components/widgets/Trips/TripDisplayStatus';
import TripsFilters, { countActiveFilters } from '../../components/widgets/Trips/TripsFilters';
import MassBonusPicker from '../../components/widgets/Trips/MassBonusPicker';
import MassBonusDialog from '../../components/widgets/Trips/MassBonusDialog';

const HIDDEN_COLUMNS_STORAGE_KEY = 'trips-daily-hidden-columns';

const TOGGLEABLE_COLUMNS = TRIP_COLUMNS.filter((c) => c.headerName && !c.hide);

const loadHiddenColumns = () => {
  try {
    const raw = localStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const GROUP_COLUMN = {
  field: 'displayStatus',
  groupBy: true,
  groupExpanded: true,
  getGroupCount: ({ value, rows }) => {
    return rows.filter((r) => r.displayStatus === value && !r.isPrevious && !r.isNext).length;
  },
  getGroupOrder: ({ value }) => {
    return (
      [
        TripDisplayStatus.unknown,
        TripDisplayStatus.needs_attention,
        TripDisplayStatus.extended_bonus,
        TripDisplayStatus.finding_driver,
        TripDisplayStatus.in_progress,
        TripDisplayStatus.finalizing_details,
        TripDisplayStatus.completed,
        TripDisplayStatus.cancelled,
      ].indexOf(value) + 1
    );
  },
  renderGroupHeader: ({ value, count }) => {
    return (
      <Stack direction="row" spacing="10px">
        <TripDisplayStatusIcon status={value} />
        <Typography>{value}</Typography>
        <Typography color="text.secondary">({count})</Typography>
      </Stack>
    );
  },
};

const updateURLParams = (
  { date, citiesIds, tripId, fullScreen, ghostTripsOnly, pendingReassignmentOnly, status, view, rideId },
  history,
) => {
  const params = new URLSearchParams();
  params.append('date', date);
  if (view === 'map') params.append('view', 'map');
  citiesIds.length && params.append('citiesIds', citiesIds.join(','));
  if (status) params.append('status', status);
  if (ghostTripsOnly) params.append('ghostTripsOnly', ghostTripsOnly);
  if (pendingReassignmentOnly) params.append('pendingReassignmentOnly', pendingReassignmentOnly);
  if (tripId) {
    params.append('tripId', tripId);
    params.append('fullScreen', !!fullScreen);
    if (view === 'map' && rideId) params.append('rideId', rideId);
  }
  history.push({ search: params.toString() });
};

const TripsDaily = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme();
  // Below `md` the eleven-column grid is replaced by a card list — see
  // TripsMobileList for why narrowing the table itself isn't the fix.
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState(loadHiddenColumns);
  // Mass bonus is two steps: the picker (which trips), then the dialog (how much).
  // `selectedTripIds` outlives both, so a cancelled amount or a partial failure
  // reopens the picker with the same picks.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedTripIds, setSelectedTripIds] = useState([]);
  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
  const canManualBonus = useHasPermission('manual_bonus');
  // Full ADMINs may override the Needs Attention rule and mass bonus any trip on
  // screen. The hook passes ADMIN for any permission, so asking for 'ADMIN' itself
  // is true for ADMINs alone. Off until they tick it, for every selection.
  const isAdmin = useHasPermission('ADMIN');
  const [anyStatus, setAnyStatus] = useState(false);
  const toast = useToast();
  const f = useFormatters();

  const toggleColumn = (field) => {
    setHiddenColumns((prev) => {
      const next = prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field];
      try {
        localStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  let { date, tripId, citiesIds, fullScreen, ghostTripsOnly, pendingReassignmentOnly, status, view, rideId } =
    useQuery();
  const mapView = view === 'map';
  date = date || todayInOpsTimezone(); //default to today in Central, not the viewer's zone
  citiesIds = (citiesIds || '').split(',').filter((id) => !!id);
  ghostTripsOnly = ghostTripsOnly === 'true';
  pendingReassignmentOnly = pendingReassignmentOnly === 'true';
  // A status this build doesn't know about (a stale bookmark, a renamed status)
  // would otherwise filter everything away with no way to tell why — drop it and
  // show the unfiltered day instead.
  status = TripDisplayStatusOrder.includes(status) ? status : null;

  const { data, date: loadedDate, loading: loadingData } = useSelector((state) => state.trips.dailyTrips);
  const { matchSet: exclusionMatchSet, isError: exclusionMatchError } = useActiveExclusionMatchSet();

  const citiesOptions = useMemo(() => {
    const tripsCities = data.reduce((cities, t) => [...cities, t.originCity, t.destinationCity], []);
    return uniqBy(tripsCities, (c) => c.id);
  }, [data]);

  // The Status dropdown's per-option counts come from everything EXCEPT the status
  // filter — otherwise picking one status would zero out every other option and
  // there'd be no way to see where the trips went. Computed on each render like the
  // filtering right below it: both are O(n) over one day of trips.
  const statusCounts = TripDisplayStatusOrder.reduce((map, s) => ({ ...map, [s]: 0 }), {});
  filterDailyTrips(data, { citiesIds, ghostTripsOnly, pendingReassignmentOnly }).forEach((t) => {
    if (statusCounts[t.displayStatus] != null) statusCounts[t.displayStatus] += 1;
  });

  const filteredData = filterDailyTrips(data, {
    citiesIds,
    ghostTripsOnly,
    pendingReassignmentOnly,
    displayStatus: status,
  });

  const sortedData = [...filteredData].sort((d1, d2) => Date.parse(d1.departureTime) - Date.parse(d2.departureTime));

  // Selection is over what's on screen, and it's re-derived from the live trip
  // list on every render rather than held as a snapshot: a trip that gets claimed
  // mid-selection leaves Needs Attention, and it must drop out of the selection
  // too instead of being bonused after the fact (the ADMIN override drops claimed
  // trips the same way).
  const eligibleTrips = getMassBonusTrips(sortedData, { anyStatus: isAdmin && anyStatus });
  // What the override would offer — so an ADMIN can open the picker on a day with
  // nothing in Needs Attention.
  const bonusableTrips = isAdmin ? getMassBonusTrips(sortedData, { anyStatus: true }) : eligibleTrips;
  const selectedTrips = eligibleTrips.filter((t) => selectedTripIds.includes(t.id));
  const eligibleIdsKey = eligibleTrips.map((t) => t.id).join(',');

  const resetMassBonus = () => {
    setPickerOpen(false);
    // Functional form, and it hands back the same array when there's nothing to
    // clear: this also runs on mount and on every filter change, and a fresh `[]`
    // there would re-render for no reason.
    setSelectedTripIds((prev) => (prev.length ? [] : prev));
    setBonusDialogOpen(false);
    setAnyStatus(false);
  };

  useEffect(() => {
    getTags()(dispatch);
  }, []);

  useEffect(() => {
    if (loadedDate !== date) {
      getDailyTrips(date)(dispatch);
    }
  }, [date]);

  // Changing the day or a filter changes which trips are on screen, so a
  // selection made against the old view no longer means what it looked like it
  // meant. Drop it rather than carry invisible picks into a bulk write.
  const citiesFilterKey = citiesIds.join(',');
  useEffect(() => {
    resetMassBonus();
  }, [date, status, citiesFilterKey, ghostTripsOnly, pendingReassignmentOnly]);

  // Trips arrive over a live subscription, so one can leave Needs Attention while
  // it is selected. Deriving `selectedTrips` hides it, but the id lingers — and if
  // the trip comes back during the same selection it would silently be selected
  // again, without the operator ever picking it. Drop the id instead of hiding it.
  useEffect(() => {
    // Read off the key rather than the array so this depends on the ids alone —
    // `eligibleTrips` is a fresh array every render.
    const eligible = new Set(eligibleIdsKey ? eligibleIdsKey.split(',') : []);
    setSelectedTripIds((prev) => {
      const next = prev.filter((id) => eligible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [eligibleIdsKey]);

  const baseParams = {
    date,
    citiesIds,
    tripId,
    fullScreen,
    ghostTripsOnly,
    pendingReassignmentOnly,
    status,
    view,
    rideId,
  };

  // List and map are two views of the same filtered day; switching keeps the
  // filters and drops the selection, which means something different in each.
  const onViewChange = (next) =>
    updateURLParams(
      { ...baseParams, view: next, tripId: undefined, fullScreen: undefined, rideId: undefined },
      history,
    );

  const onDateChange = (e) => updateURLParams({ ...baseParams, date: e.target.value }, history);

  const onRowClick = ({ row }) => updateURLParams({ ...baseParams, tripId: row.id, fullScreen: true }, history);

  const onTripDetailsClose = () =>
    updateURLParams({ ...baseParams, tripId: undefined, fullScreen: undefined }, history);

  const onTripDetailsFullscreen = (fullScreen) => updateURLParams({ ...baseParams, fullScreen }, history);

  const filters = { status, citiesIds, ghostTripsOnly, pendingReassignmentOnly };
  const onFiltersChange = (patch) => updateURLParams({ ...baseParams, ...patch }, history);

  const trip = data.find((t) => t.id === tripId);

  const viewToggle = (
    <Box sx={{ width: 120, flexShrink: 0 }}>
      <SegmentedFilter
        value={mapView ? 'map' : 'list'}
        onChange={onViewChange}
        segments={[
          { key: 'list', label: 'List' },
          { key: 'map', label: 'Map' },
        ]}
      />
    </Box>
  );

  // On a phone in map view this lives in the bottom sheet instead, so the map
  // can have the whole screen — see TripsMapView.
  const header = (
    <Box sx={cardHeaderSx}>
      {/* One row on desktop. On a phone the title takes its own line and the
          controls wrap onto as many lines as they need below it — squeezed onto
          one line they overflowed the card. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: '12px', md: 2 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Stack direction="row" alignItems="center" spacing="12px" sx={{ flex: 1, minWidth: 0 }}>
          {/* Grows to push the mobile date pill right, but never shrinks: with the
              default `flex-shrink: 1` a tight desktop toolbar squeezed this down and
              broke it across two lines ("All / trips"). */}
          <Typography
            sx={{
              fontSize: { xs: '17px', md: '19px' },
              fontWeight: 600,
              letterSpacing: '-0.01em',
              flex: 1,
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            All trips
          </Typography>
          {!compact && viewToggle}
          {exclusionMatchError && (
            <Tooltip
              title="Couldn't load the Rider-Driver Exclusions list — excluded pairs may not be flagged below."
              arrow
            >
              <span style={{ display: 'inline-flex' }}>
                <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
              </span>
            </Tooltip>
          )}
          {/* On a phone the date rides on the title line rather than joining the
              wrapped pills below — it's the control ops changes most, and the
              title is short enough to leave room for it. */}
          {compact && <DatePicker pill id="trips_date" value={date} onChange={onDateChange} />}
        </Stack>
        {/* A flex box with `gap`, not a Stack: Stack spaces children with
            margin-left, which breaks the moment a row wraps. The pills must not
            shrink either — their labels are `flexShrink: 0` inside, so a
            shrinking pill just lets its own text spill over the next one. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            // Wraps at every width, not just when compact: a second row reads
            // better than an overflowing one. Right-aligned there so a wrapped row
            // still lines up with the card edge.
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            gap: { xs: '8px', md: '16px' },
            minWidth: 0,
            '& > *': { flexShrink: 0 },
          }}
        >
          {/* On a phone the title line already carries the date; the toggle
              leads the pills instead of overflowing it. */}
          {compact && viewToggle}
          {canManualBonus && !mapView && (
            // A bare disabled pill says nothing about why — and the filters can
            // change the answer, so say it.
            <Tooltip
              title={
                bonusableTrips.length ? '' : isAdmin ? 'No trips to bonus' : 'No trips in Needs Attention to bonus'
              }
              arrow
            >
              <span style={{ display: 'inline-flex' }}>
                <Button sx={filterPillSx} disabled={!bonusableTrips.length} onClick={() => setPickerOpen(true)}>
                  Mass bonus
                </Button>
              </span>
            </Tooltip>
          )}
          <Badge badgeContent={countActiveFilters(filters)} color="primary">
            <Button endIcon={<ExpandMoreIcon />} onClick={(e) => setFilterAnchorEl(e.currentTarget)} sx={filterPillSx}>
              Filters
            </Button>
          </Badge>
          {/* Column visibility only means something for the grid, which isn't the
              rendering below `md`. */}
          {!compact && !mapView && (
            <Button endIcon={<ExpandMoreIcon />} onClick={(e) => setColumnsAnchorEl(e.currentTarget)} sx={filterPillSx}>
              Columns
            </Button>
          )}
          <Popover
            open={Boolean(columnsAnchorEl)}
            anchorEl={columnsAnchorEl}
            onClose={() => setColumnsAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: '8px', ...floatSurfaceSx } }}
          >
            <Stack sx={{ padding: '12px 16px', minWidth: 220 }}>
              {TOGGLEABLE_COLUMNS.map((c) => (
                <FormControlLabel
                  key={c.field}
                  control={
                    <Checkbox
                      sx={{ pr: 0 }}
                      checked={!hiddenColumns.includes(c.field)}
                      onChange={() => toggleColumn(c.field)}
                    />
                  }
                  label={c.headerName}
                />
              ))}
            </Stack>
          </Popover>
          <TripsFilters
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            value={filters}
            onChange={onFiltersChange}
            statusCounts={statusCounts}
            citiesOptions={citiesOptions}
          />
          {!compact && <DatePicker pill id="trips_date" value={date} onChange={onDateChange} />}
        </Box>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { md: '100%' } }}>
      {!(mapView && compact) && header}
      {mapView ? (
        <TripsMapView
          trips={sortedData}
          // Unfiltered, so a linked trip still opens when the filters hide it.
          allTrips={data}
          loading={loadingData}
          tripId={tripId}
          rideId={rideId}
          listHeader={compact ? header : null}
          onNavigate={(next) => updateURLParams({ ...baseParams, ...next }, history)}
        />
      ) : compact ? (
        // Flows in the document rather than scrolling inside a fixed-height box:
        // on a phone the page owns the scroll (see flushPageSx).
        <TripsMobileList
          data={sortedData}
          // Unfiltered, for pair resolution only — see TripsMobileList.
          allTrips={data}
          exclusionMatchSet={exclusionMatchSet}
          loading={loadingData}
          onRowClick={onRowClick}
        />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <TripsTable
              data={sortedData}
              loading={loadingData}
              onRowClick={onRowClick}
              ghostTripsOnly={ghostTripsOnly}
              getColumn={(c) => {
                let col = c.field === GROUP_COLUMN.field ? { ...c, ...GROUP_COLUMN } : c;
                if (hiddenColumns.includes(c.field)) {
                  col = { ...col, hide: true };
                }
                return col;
              }}
              getRow={(row) => ({
                ...row,
                // HITCH-5300: which of this trip's riders (if any) are excluded
                // with its driver — empty for an unclaimed trip, since there's
                // no formed pairing yet.
                excludedRiderIds: getExcludedRiderIds(
                  row.driver?.id,
                  (row.rides || []).map((ride) => ride.rider),
                  exclusionMatchSet,
                ),
              })}
            />
          </Box>
        </Box>
      )}
      <MassBonusPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        trips={eligibleTrips}
        selectedIds={selectedTripIds}
        onChange={setSelectedTripIds}
        canOverride={isAdmin}
        anyStatus={isAdmin && anyStatus}
        onAnyStatusChange={setAnyStatus}
        onContinue={() => {
          setPickerOpen(false);
          setBonusDialogOpen(true);
        }}
      />
      <MassBonusDialog
        open={bonusDialogOpen}
        trips={selectedTrips}
        onClose={() => setBonusDialogOpen(false)}
        onApplied={async (summary, amount) => {
          // Re-read the day rather than patching rows locally, and treat the
          // reloaded trips as the source of truth rather than the responses: the
          // endpoint answers 200 even when the write silently failed, because
          // Trip.setBonus catches its own save error. A trip that came back 200
          // but reloaded with its old bonus did NOT get bonused.
          const { success, data } = await getDailyTrips(date)(dispatch);

          // The reload is the verification, so a failed reload verifies nothing.
          // Without this branch `data` is undefined, every 200 reads as
          // persisted, and ops get a success toast with the selection cleared
          // while the day table sits empty -- the exact failure this
          // reload-and-compare was added to catch.
          if (!success) {
            toast.error(
              `Couldn't reload the day to confirm the bonus - ${summary.appliedCount} ${
                summary.appliedCount === 1 ? 'trip' : 'trips'
              } still selected. Refresh and check before retrying.`,
            );
            return;
          }

          const unresolved = findUnresolvedTrips(summary, amount, data);

          if (!unresolved.length) {
            toast.success(
              `Applied ${f.number.currency(amount)} to ${summary.appliedCount} ${
                summary.appliedCount === 1 ? 'trip' : 'trips'
              }.`,
            );
            resetMassBonus();
            return;
          }
          // Leave the unresolved trips selected so the next Mass bonus retries them.
          toast.error(
            `${unresolved.length} ${unresolved.length === 1 ? 'trip' : 'trips'} did not take the bonus — still selected.`,
          );
          setSelectedTripIds(unresolved);
        }}
      />
      {trip && !mapView && (
        <TripDetailsDialog
          trip={trip}
          fullScreen={fullScreen === 'true'}
          outboundTrip={tripId ? data.find((t) => t.nextTrip === tripId) : null}
          returnTrip={tripId ? data.find((t) => t.previousTrip === tripId) : null}
          onClose={onTripDetailsClose}
          onFullscreen={onTripDetailsFullscreen}
        />
      )}
    </Box>
  );
};

export default TripsDaily;
